import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { HOST } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Skyline stays,", "hosted with", "uncommon care."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-end gap-12 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#E4DBD0] bg-[#F6F1E8] px-4 py-2"
            data-testid="hero-host-badge"
          >
            <Star className="h-3.5 w-3.5 fill-[#C4623C] text-[#C4623C]" />
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[#63584E]">
              Superhost · {HOST.rating} · {HOST.reviewsTotal} reviews
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline-text"
            className="font-serif-display text-[15vw] leading-[0.98] tracking-tight text-[#201B17] sm:text-7xl lg:text-[6.4rem]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.45 + i * 0.14, ease: EASE }}
                >
                  {i === 2 ? (
                    <>
                      <em className="text-[#C4623C]">uncommon</em> care.
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-[#63584E] md:text-lg"
          >
            A husband-and-wife Superhost team crafting calm, sky-high homes in
            the heart of Melbourne — so you can unwind, recharge, and feel just
            like home.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={HOST.airbnbProfile}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-airbnb-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#C4623C] px-7 py-4 text-sm font-medium text-[#FBF8F3] transition-colors duration-300 hover:bg-[#201B17]"
            >
              View Airbnb profile
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#properties"
              data-testid="hero-explore-cta"
              className="link-line font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[#201B17]"
            >
              Explore the collection
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.3, delay: 0.7, ease: EASE }}
            className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-[#E4DBD0]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HOST.heroImage}
              alt="ALMAH Stays Melbourne CBD apartment with skyline views"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
            className="absolute -bottom-6 -left-4 rounded-2xl border border-[#E4DBD0] bg-[#FBF8F3]/90 px-6 py-4 shadow-[0_20px_50px_-20px_rgba(32,27,23,0.35)] backdrop-blur-md md:-left-10"
            data-testid="hero-location-tag"
          >
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#8C7F72]">
              Hosting from
            </p>
            <p className="font-serif-display mt-1 text-lg leading-tight text-[#201B17]">
              {HOST.location}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
